import React from 'react';
import {Image, StyleSheet, View} from 'react-native';
import Svg, {Path, Rect} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {PlayerProfilePrivate} from '@/Hooks/PlayerProfileApi/types';

const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 48,
    paddingHorizontal: 32,
    alignItems: 'center',
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: '#3DAB9E',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {width: 88, height: 88},
  avatarInitials: {color: '#fff', fontSize: 30, fontWeight: '600'},
  name: {fontSize: 20, fontWeight: '600', color: '#262322', marginTop: 16},
  handle: {fontSize: 13, color: '#888280', marginTop: 2},
  lockCircle: {
    width: 54,
    height: 54,
    borderRadius: 27,
    backgroundColor: '#FAF6EB',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 22,
  },
  title: {fontSize: 17, fontWeight: '600', color: '#262322', marginTop: 14},
  subtitle: {
    fontSize: 13.5,
    color: '#888280',
    marginTop: 6,
    textAlign: 'center',
    maxWidth: 380,
    lineHeight: 20,
  },
});

function LockIcon() {
  return (
    <Svg width={26} height={26} viewBox="0 0 24 24" fill="none">
      <Rect x={4} y={11} width={16} height={10} rx={2.5} stroke="#4D3F37" strokeWidth={2} />
      <Path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="#4D3F37" strokeWidth={2} />
    </Svg>
  );
}

type Props = {
  profile: PlayerProfilePrivate;
};

// Instagram-style "This profile is private" state, shown to any non-owner
// viewer (anonymous or logged-in) of a profile whose owner has marked it
// private. Only enough identity to say whose profile it is.
export default function PrivateProfileNotice({profile}: Props) {
  const name = profile.name || profile.username || 'This player';
  return (
    <Box style={styles.card}>
      <View style={styles.avatar}>
        {profile.avatar_url ? (
          <Image source={{uri: profile.avatar_url}} style={styles.avatarImage} />
        ) : (
          <CCText style={styles.avatarInitials}>{initials(name)}</CCText>
        )}
      </View>
      <CCText style={styles.name}>{name}</CCText>
      {profile.username ? (
        <CCText style={styles.handle}>@{profile.username}</CCText>
      ) : null}

      <View style={styles.lockCircle}>
        <LockIcon />
      </View>
      <CCText style={styles.title}>This profile is private</CCText>
      <CCText style={styles.subtitle}>
        {name} has chosen to keep their games, stats and clubs private. Only
        they can view this profile.
      </CCText>
    </Box>
  );
}
